import { useEffect, useState } from 'react';
import { Menu, Search, Command as CommandIcon, Keyboard, Rows3, Rows4 } from 'lucide-react';
import { TickerTape } from '@/widgets/TickerTape';
import { CommandPalette } from './CommandPalette';
import { ShortcutsHelp } from './ShortcutsHelp';
import { MobileSidebar } from './MobileSidebar';
import { useDensity } from '@/lib/useDensity';

export function TopBar() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [density, , toggleDensity] = useDensity();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if (e.key === '?' && !(e.target instanceof HTMLInputElement)) {
        setHelpOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className="flex flex-col border-b border-zinc-800/80 bg-zinc-950/60 backdrop-blur-sm sticky top-0 z-30">
      <div className="border-b border-zinc-900">
        <TickerTape />
      </div>
      <div className="flex items-center gap-3 px-4 py-2">
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          className="md:hidden p-2.5 rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
          aria-label="Open navigation"
        >
          <Menu className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="group flex items-center gap-2 flex-1 max-w-xl px-3 py-1.5 rounded-md border border-zinc-800 bg-zinc-900/60 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200 transition-colors text-sm"
        >
          <Search className="size-4" />
          <span>Search tickers, navigate, toggle rules</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-zinc-400">
            <CommandIcon className="size-3" /> K
          </span>
        </button>
        <button
          type="button"
          onClick={toggleDensity}
          className="p-2.5 rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors hidden sm:inline-flex"
          title={`Density: ${density}`}
          aria-label={`Toggle density (currently ${density})`}
        >
          {density === 'comfortable' ? <Rows3 className="size-4" /> : <Rows4 className="size-4" />}
        </button>
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          className="p-2.5 rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
          title="Keyboard shortcuts"
          aria-label="Show keyboard shortcuts"
        >
          <Keyboard className="size-4" />
        </button>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <ShortcutsHelp open={helpOpen} onOpenChange={setHelpOpen} />
      <MobileSidebar open={navOpen} onOpenChange={setNavOpen} />
    </header>
  );
}
